// ProposalViewer.jsx

import React, { useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';

// CSS
import 'nodetypes/purchaseorder/view/PurchaseOrderViewer.css';
import './ProposalViewer.css';

// Components
import Button from 'components/button/Button';
import DocumentPayments from 'nodetypes/documents/payments/DocumentPayments';
import DocumentRichTextSection from 'nodetypes/documents/kit/DocumentRichTextSection';
import useDocumentRelatedNodes from 'nodetypes/documents/hooks/useDocumentRelatedNodes';
import ProposalGeneralInfo from 'nodetypes/proposal/view/ProposalGeneralInfo';
import ProposalItems from 'nodetypes/proposal/view/ProposalItems';
import ProposalPreviewModal from 'nodetypes/proposal/view/ProposalPreviewModal';

/**
 * ProposalViewer
 *
 * Workspace Highlight-slot screen for PROPOSAL.
 *
 * @param {object} node
 * @param {object|null} [nodeType]
 * @param {boolean} [readOnly=false]
 * @param {function} [onNodeChange]
 * @param {function|null} [onRelatedNodeOpen]
 * @param {object[]|null} [neighborNodes]
 * @param {object|null} [letterheadOverride]
 * @returns {ReactNode}
 */
const ProposalViewer = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  onNodeChange,
  onRelatedNodeOpen = null,
  neighborNodes: neighborNodesProp = null,
  letterheadOverride = null,
}) => {
  // ----- CONTEXT ----- //

  const { activeBase } = useBase();

  // ----- STATE ----- //

  // Preview modal open state
  const [previewOpen, setPreviewOpen] = useState(false);

  // ----- MEMOS / HOOKS ----- //

  const baseKey = String(activeBase?.key || node?.baseKey || '').trim();
  const subscribedNeighbors = useDocumentRelatedNodes(
    neighborNodesProp == null ? node?.key : null,
    neighborNodesProp == null ? baseKey : null,
  );
  const neighborNodes = neighborNodesProp == null ? subscribedNeighbors : neighborNodesProp;

  if (!node) return null;

  // ----- RENDER ----- //

  return (
    <div className="purchase-order-viewer">
      <div className="proposal-viewer-toolbar">
        <Button
          label="Preview"
          iconKey="visibility"
          height="30px"
          onClick={() => setPreviewOpen(true)}
        />
      </div>

      <section className="purchase-order-viewer-section">
        <div className="purchase-order-viewer-section-header">
          <div className="purchase-order-viewer-section-title">
            General Info
          </div>
        </div>
        <ProposalGeneralInfo
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          onNodeChange={onNodeChange}
          letterheadOverride={letterheadOverride}
        />
      </section>

      <section className="purchase-order-viewer-section">
        <ProposalItems
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          neighborNodes={neighborNodes}
          onRelatedNodeOpen={onRelatedNodeOpen}
        />
      </section>

      <section className="purchase-order-viewer-section">
        <DocumentPayments
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          neighborNodes={neighborNodes}
          onRelatedNodeOpen={onRelatedNodeOpen}
        />
      </section>

      <section className="purchase-order-viewer-section">
        <DocumentRichTextSection
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          onNodeChange={onNodeChange}
          fieldName="termsAndConditions"
          title="Terms and Conditions"
          emptyLabel="No terms and conditions."
        />
      </section>

      <section className="purchase-order-viewer-section">
        <DocumentRichTextSection
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          onNodeChange={onNodeChange}
          fieldName="memo"
          title="Memo"
          emptyLabel="No memo."
        />
      </section>

      <ProposalPreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        node={node}
        nodeType={nodeType}
        neighborNodes={neighborNodes}
        letterheadOverride={letterheadOverride}
      />
    </div>
  );
});

// Dev Tools
ProposalViewer.displayName = 'ProposalViewer';

export default ProposalViewer;
